/** @format */
import { css } from 'styled-components';

import { smallTablet } from '@toucan/client/global/application';

import { ModalSize, ModalVariant } from './constants';
import { curveDisplay } from './style-helpers';
import { ModalProps } from './types';

const BORDER_RADIUS = '1.6rem';

const smallModalStyles = css`
    .contents {
        padding: var(--layout-spacing-32) var(--layout-spacing-16);
    }
`;

const smallModalHolographicVariantStyles = css`
    /* blend the background into the curved close button */
    &:before {
        content: '';
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        border-radius: ${BORDER_RADIUS};
        background: radial-gradient(
            circle at top center,
            white 5rem,
            transparent 10rem
        );
    }
`;

/**
 * Base styles for the modal component
 *
 * @param {ModalProps} props the modal props
 * */
const baseStyles = (props: ModalProps) => css`
    --holographic-background__border-radius: ${BORDER_RADIUS};

    background: var(--colors-surface-primary);
    border-radius: ${BORDER_RADIUS};
    position: fixed;
    display: flex;
    top: 50%;
    left: 50%;
    transform: translate3d(-100%, -100%, 0);
    z-index: 9999;
    max-width: calc(100% - var(--layout-spacing-40));
    max-height: calc(100% - var(--layout-spacing-40));

    .header {
        display: flex;
        justify-content: center;
        margin: 0;
        top: calc(var(--layout-spacing-8) * -1);
        align-items: flex-end;
        position: absolute;
        right: 0;
        left: 0;
        z-index: 3;

        .curved-svg {
            position: absolute;
            display: ${curveDisplay};
            width: 8.2rem;
            top: calc(var(--layout-spacing-8) * -1);
        }
    }

    .contents {
        flex: 1 1 auto;
        overflow-y: auto;
        text-align: left;
        padding: var(--layout-spacing-40);
        z-index: 1;
    }

    .auth-link-error-heading {
        text-align: center;
    }

    ${smallTablet(smallModalStyles)}
`;

/**
 * Size styles for the modal component
 *
 * @param {ModalProps} props the modal props
 * */
const sizeStyles = ({ size }: ModalProps) => {
    switch (size) {
        case ModalSize.FULLSCREEN:
            return css`
                max-width: 100%;
                height: 100%;
                width: 100%;
                max-height: 100%;
                border-radius: 0;
                padding: 0;

                .contents {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }

                .header {
                    margin: var(--layout-spacing-24);
                    justify-content: flex-end;
                }

                ${smallTablet(css`
                    .header {
                        margin: var(--layout-spacing-12);
                    }
                `)}
            `;

        case ModalSize.LARGE:
            return css`
                width: 72rem;
            `;

        case ModalSize.MEDIUM:
            return css`
                width: 60rem;
            `;

        case ModalSize.SMALL:
            return css`
                width: 36rem;

                ${smallModalStyles}
            `;

        default:
            return css``;
    }
};

const variantStyles = ({ variant, size }: ModalProps) => {
    if (variant === ModalVariant.HOLOGRAPHIC) {
        return css`
            .holographic-background {
                /* fade to white at the bottom of the modal */
                &::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    overflow: hidden;
                    border-radius: ${BORDER_RADIUS};
                    background: linear-gradient(
                        to bottom,
                        transparent 0%,
                        white 100%
                    );
                }
            }

            ${size === ModalSize.SMALL && smallModalHolographicVariantStyles}
            ${smallTablet(smallModalHolographicVariantStyles)}
        `;
    }

    return css``;
};

/**
 * The modal component styles
 *
 * @param {ModalProps} props The modal properties object
 * */
export const styles = (props: ModalProps) => css`
    ${baseStyles(props)}
    ${sizeStyles(props)}
    ${variantStyles(props)}
`;
